import React, { useEffect } from "react";
import { Modal } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";

import CreateDocument, {
  IDocumentCreateForm,
} from "~/components/Forms/CreateDocument";
import Preloader from "~/components/Preloader";
import {
  dataToRequest_CreateDocument,
  defaultDocumentValue_CreteDocument,
  defaultDocumentValueFromTDocument_CreteDocument,
  DocumentStatus,
} from "~/components/Forms/CreateDocument/utils/utils";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { userHaveAccessToDocumentEdit } from "~/pages/Documents/utils/utils";
import {
  Section,
  useCreateDocumentMutation,
  useEditDocumentMutation,
  useGetDocumentLazyQuery,
  useGetProfileLazyQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePermissions } from "~/shared/context/PermissionContext";

const documentFormId = "DocumentForm";

enum ModalState {
  edit = "Edit",
  create = "Create",
}

interface IDocumentModal {
  catalog: Section;
  reFetchTable?: () => void;
}

const DocumentModal = ({ catalog, reFetchTable }: IDocumentModal) => {
  const setDataReplace = useSetLocationData(true);
  const { openModal, currentId } = useGetLocationData();

  const editModalIsOpen =
    openModal === ModalState.create || openModal === ModalState.edit;

  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const { notifySuccess } = useNotify();

  const handleCloseModal = () =>
    setDataReplace("openModal", "", "currentId", "");

  const onCompletedHandler = (content: string) => () => {
    notifySuccess(content);
    handleCloseModal();
    reFetchTable?.();
  };
  const onCompletedCrete = onCompletedHandler(
    t("documents.notification.createSuccess"),
  );
  const onCompletedEdit = onCompletedHandler(
    t("documents.notification.editSuccess"),
  );

  const [getProfile, getProfileState] = useGetProfileLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [getDocument, getDocumentState] = useGetDocumentLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [createDocument] = useCreateDocumentMutation({
    onCompleted: onCompletedCrete,
  });
  const [editDocument] = useEditDocumentMutation({
    onCompleted: onCompletedEdit,
  });

  useEffect(() => {
    if (editModalIsOpen && !!currentId) {
      getProfile();
      getDocument({
        variables: {
          id: currentId,
        },
      });
    }
  }, [editModalIsOpen, currentId]);

  // Берём документ из ответа только если он совпадает с открытым currentId,
  // иначе при повторном открытии модалки могут мелькнуть данные прошлого документа
  const loadedDocument = get(getDocumentState.data, "DocumentGetter.getOne");
  const documentInstance =
    editModalIsOpen && !!currentId && loadedDocument?.id === currentId
      ? loadedDocument
      : undefined;

  const cantEdit =
    !!getProfileState.data && !!documentInstance
      ? userHaveAccessToDocumentEdit(
          getProfileState.data,
          documentInstance,
          hasPermission("documents.edit"),
        )
      : false;

  const onSubmit = (data: IDocumentCreateForm) => {
    documentInstance
      ? editDocument({
          variables: {
            ...dataToRequest_CreateDocument(data),
            sectionId: catalog.id,
            id: currentId,
            status: documentInstance.status,
          },
        })
      : createDocument({
          variables: {
            ...dataToRequest_CreateDocument(
              data,
              documentInstance?.status as DocumentStatus,
            ),
            sectionId: catalog.id,
          },
        });
  };

  const modalTitle =
    openModal === ModalState.edit
      ? t("documentLib.documentEdit")
      : t("documentLib.documentCreate");

  return (
    <Modal
      open={editModalIsOpen}
      title={modalTitle}
      onCancel={handleCloseModal}
      closable
      centered
      destroyOnHidden
      okButton={{
        htmlType: "submit",
        form: documentFormId,
        children: t("ui.save"),
      }}
    >
      {!!currentId ? (
        documentInstance && !getDocumentState.loading ? (
          <CreateDocument
            formId={documentFormId}
            cantEdit={cantEdit}
            isEdit={!!currentId}
            defaultValues={defaultDocumentValueFromTDocument_CreteDocument(
              documentInstance,
            )}
            defaultSubdivisionName={documentInstance.Subdivision?.name}
            onSubmitHandler={onSubmit}
          />
        ) : (
          <Preloader />
        )
      ) : (
        <CreateDocument
          formId={documentFormId}
          cantEdit={cantEdit}
          isEdit={!!currentId}
          defaultValues={{ ...defaultDocumentValue_CreteDocument }}
          onSubmitHandler={onSubmit}
        />
      )}
    </Modal>
  );
};

export default DocumentModal;
